import { useCallback, useEffect, useMemo, useState } from "react";
import { MenuAnalyticsMealPeriod, MenuAnalyticsTimeRange, MenuItemsSortField, SortDirection } from "../types";

// Centralized filter / sort / pagination state for the analytics dashboard.
// Lives above the tabs so selections persist when switching between the Menu
// Items and Ingredients tabs. Any filter change resets to page 1;
export interface SharedAnalyticsFilters {
  timeRange: MenuAnalyticsTimeRange;
  startDate?: string;
  endDate?: string;
  mealPeriod: MenuAnalyticsMealPeriod;
  teamId?: string;
}

export interface AnalyticsFilterState extends SharedAnalyticsFilters {
  sortBy: MenuItemsSortField;
  sortDir: SortDirection;
  page: number;
  pageSize: number;
}

const INITIAL: SharedAnalyticsFilters = {
  timeRange: "lastWeek",
  mealPeriod: "all",
};

// Persisted for the browser tab's session only, so a fresh session still opens
// on the "lastWeek" default while navigating away and back keeps the user's pick.
const STORAGE_KEY = "menuAnalytics.filters";

const VALID_TIME_RANGES: MenuAnalyticsTimeRange[] = ["today", "thisWeek", "lastWeek", "thisMonth", "lastMonth", "custom"];
const VALID_MEAL_PERIODS: MenuAnalyticsMealPeriod[] = ["all", "breakfast", "lunch", "dinner", "snack"];

const isValidStoredFilters = (parsed: unknown): parsed is SharedAnalyticsFilters => {
  if (typeof parsed !== "object" || parsed === null) {
    return false;
  }
  const p = parsed as Record<string, unknown>;
  if (!VALID_TIME_RANGES.includes(p.timeRange as MenuAnalyticsTimeRange)) {
    return false;
  }
  if (!VALID_MEAL_PERIODS.includes(p.mealPeriod as MenuAnalyticsMealPeriod)) {
    return false;
  }
  if (p.timeRange === "custom" && !(typeof p.startDate === "string" && typeof p.endDate === "string")) {
    return false;
  }
  return p.teamId === undefined || typeof p.teamId === "string";
};

// sessionStorage holds our own JSON, but we still validate on read: a stale shape
// from a future release, or manual tampering, shouldn't get passed straight through
// to the analytics query — fall back to INITIAL instead.
const readStoredFilters = (): SharedAnalyticsFilters => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return INITIAL;
    }
    const parsed = JSON.parse(raw);
    return isValidStoredFilters(parsed) ? parsed : INITIAL;
  } catch {
    return INITIAL;
  }
};

export interface UseAnalyticsFilters {
  filters: SharedAnalyticsFilters;
  filtersKey: string;
  setTimeRange: (timeRange: MenuAnalyticsTimeRange) => void;
  setCustomRange: (startDate: string, endDate: string) => void;
  setMealPeriod: (mealPeriod: MenuAnalyticsMealPeriod) => void;
  setTeamId: (teamId: string | undefined) => void;
}

export const useAnalyticsFilters = (): UseAnalyticsFilters => {
  const [filters, setFilters] = useState<SharedAnalyticsFilters>(readStoredFilters);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // Best-effort persistence (e.g. Safari private mode throws on setItem) — the
      // in-memory filter state above is unaffected either way.
    }
  }, [filters]);

  const patch = useCallback((next: Partial<SharedAnalyticsFilters>) => setFilters(prev => ({ ...prev, ...next })), []);

  const setTimeRange = useCallback(
    (timeRange: MenuAnalyticsTimeRange) =>
      patch(timeRange === "custom" ? { timeRange } : { timeRange, startDate: undefined, endDate: undefined }),
    [patch]
  );

  const setCustomRange = useCallback(
    (startDate: string, endDate: string) => patch({ timeRange: "custom", startDate, endDate }),
    [patch]
  );

  const setMealPeriod = useCallback((mealPeriod: MenuAnalyticsMealPeriod) => patch({ mealPeriod }), [patch]);
  const setTeamId = useCallback((teamId: string | undefined) => patch({ teamId }), [patch]);

  const filtersKey = JSON.stringify(filters);

  return useMemo(
    () => ({ filters, filtersKey, setTimeRange, setCustomRange, setMealPeriod, setTeamId }),
    [filters, filtersKey, setTimeRange, setCustomRange, setMealPeriod, setTeamId]
  );
};
